import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import type {
  FeatureMockupCopy,
  LandingPageData,
  MarketingFooterData,
  MarketingNavData,
  MarketingWaitlistCopy,
} from "@/lib/marketingDefaults";
import { formatWaitlistLiveCount } from "@/lib/marketingDefaults";
import { supabase } from "@/lib/supabase";

const FALLBACK_LOGO = "/pulse-logo-mark.png";

export type MarketingStats = {
  waitlistCount: number;
  openVenuesNow: number;
  curatedVenuesTotal: number;
  updatedAt: string;
};

export function useMarketingStats(pollMs = 180_000): MarketingStats | null {
  const [stats, setStats] = useState<MarketingStats | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch("/api/marketing-stats", { cache: "no-store" });
        if (!res.ok) return;
        const data = (await res.json()) as MarketingStats;
        if (!cancelled) setStats(data);
      } catch {
        /* keep last known */
      }
    };
    void load();
    const id = window.setInterval(() => void load(), pollMs);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [pollMs]);

  return stats;
}

export function MarketingNav({
  active,
  nav,
  logoUrl,
}: {
  active?: "home" | "about";
  nav: MarketingNavData;
  logoUrl?: string;
}) {
  const src = logoUrl || FALLBACK_LOGO;
  return (
    <nav>
      <Link to="/" className="wordmark">
        <img src={src} alt={nav.logoAlt} className="logo-mark" />
        {nav.wordmark}
        <span>.</span>
      </Link>
      <div className="nav-links">
        {active === "about" ? (
          <>
            <Link to="/">{nav.aboutLinks.home}</Link>
            <a href="/#features">{nav.aboutLinks.features}</a>
            <a href="/#venues">{nav.aboutLinks.venues}</a>
            <Link to="/about" className="active">
              {nav.aboutLinks.about}
            </Link>
          </>
        ) : (
          <>
            <a href="#night">{nav.homeLinks.night}</a>
            <a href="#features">{nav.homeLinks.features}</a>
            <a href="#venues">{nav.homeLinks.venues}</a>
            <Link to="/about">{nav.homeLinks.about}</Link>
          </>
        )}
      </div>
      {active === "about" ? (
        <Link to="/" className="nav-cta">
          {nav.ctaLabel}
        </Link>
      ) : (
        <a className="nav-cta" href="#waitlist">
          {nav.ctaLabel}
        </a>
      )}
    </nav>
  );
}

export function MarketingFooter({
  footer,
  logoUrl,
}: {
  footer: MarketingFooterData;
  logoUrl?: string;
}) {
  const src = logoUrl || FALLBACK_LOGO;
  return (
    <footer>
      <div className="wordmark" style={{ fontSize: 18 }}>
        <img src={src} alt="" className="logo-mark" style={{ height: 22 }} />
        {footer.wordmark}
        <span>.</span>
      </div>
      <div className="flinks">
        <Link to="/about">{footer.aboutLabel}</Link>
        <Link to="/vendor/login">{footer.vendorLabel}</Link>
        <Link to="/user/login">{footer.loginLabel}</Link>
      </div>
    </footer>
  );
}

function WaitlistForm({
  ctaLabel,
  copy,
  liveCount,
}: {
  ctaLabel: string;
  copy: MarketingWaitlistCopy;
  liveCount: number | null;
}) {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState<"idle" | "loading" | "ok" | "err">("idle");
  const [message, setMessage] = useState("");
  const [referralLink, setReferralLink] = useState("");

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!consent) {
      setStatus("err");
      setMessage(copy.consentError);
      return;
    }
    setStatus("loading");
    setMessage("");
    try {
      const ref =
        typeof window !== "undefined"
          ? new URLSearchParams(window.location.search).get("ref") ?? undefined
          : undefined;
      const { data, error } = await supabase.functions.invoke("waitlist-signup", {
        body: { email: email.trim(), ref, consent: true },
      });
      if (error) throw error;
      const payload = data as { referral_code?: string; referral_link?: string; error?: string };
      if (payload.error) throw new Error(payload.error);
      setStatus("ok");
      setReferralLink(payload.referral_link ?? `${window.location.origin}/?ref=${payload.referral_code}`);
      setMessage(copy.successMessage);
    } catch (err) {
      setStatus("err");
      setMessage(err instanceof Error ? err.message : copy.genericError);
    }
  };

  return (
    <div>
      <form className="waitlist" id="waitlist" onSubmit={(e) => void submit(e)}>
        <input
          type="email"
          required
          placeholder={copy.placeholder}
          aria-label={copy.ariaLabel}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <button className="btn-primary" type="submit" disabled={status === "loading"}>
          {status === "loading" ? copy.loadingLabel : ctaLabel}
        </button>
      </form>
      {liveCount !== null ? (
        <p className="micro waitlist-live-count">
          {formatWaitlistLiveCount(copy.liveCountTemplate, liveCount)}
        </p>
      ) : null}
      <label className="waitlist-consent">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
        />
        <span>{copy.consentLabel}</span>
      </label>
      {message ? (
        <p className="micro waitlist-message">
          {message}
          {referralLink ? (
            <>
              <br />
              <a href={referralLink} style={{ color: "var(--amber)", wordBreak: "break-all" }}>
                {referralLink}
              </a>
            </>
          ) : null}
        </p>
      ) : null}
    </div>
  );
}

function RadarMockup({ label }: { label: string }) {
  return (
    <div className="fmock fmock-radar">
      <div className="rline" />
      <div className="rdot" style={{ top: 32, left: 38 }} />
      <div className="rdot v" style={{ top: 70, left: 110 }} />
      <div className="rdot" style={{ top: 95, left: 60 }} />
      <div className="rdot v" style={{ top: 45, left: 180 }} />
      <div className="rlabel">{label}</div>
    </div>
  );
}

function ChatMockup({ mockups }: { mockups: FeatureMockupCopy }) {
  return (
    <div className="fmock fmock-chat">
      <div className="bubble user">{mockups.chatUser}</div>
      <div className="bubble ai">{mockups.chatAi}</div>
      <div className="chips">
        {mockups.chatChips.map((chip) => (
          <span className="chip" key={chip}>
            {chip}
          </span>
        ))}
      </div>
    </div>
  );
}

function ListMockup({ mockups }: { mockups: FeatureMockupCopy }) {
  return (
    <div className="fmock fmock-list">
      {mockups.listRows.map((row) => (
        <div className="mrow" key={row.name}>
          <span className="name">
            <span className={`dot ${row.statusTone === "soon" ? "soon" : "on"}`} />
            {row.name}
          </span>
          <span>{row.status}</span>
        </div>
      ))}
    </div>
  );
}

function FeatureMockup({
  type,
  mockups,
}: {
  type: "radar" | "chat" | "list";
  mockups: FeatureMockupCopy;
}) {
  if (type === "chat") return <ChatMockup mockups={mockups} />;
  if (type === "list") return <ListMockup mockups={mockups} />;
  return <RadarMockup label={mockups.radarLabel} />;
}

export function LandingPageView({ data }: { data: LandingPageData }) {
  const cats = [...data.venueCategories, ...data.venueCategories];
  const stats = useMarketingStats();
  const logoUrl = data.brand.logoUrl;

  return (
    <div className="marketing-root" data-marketing-theme={data.themePreset}>
      <div className="pulse-rail" id="rail" />
      <MarketingNav nav={data.nav} logoUrl={logoUrl} />

      <section className="hero">
        <div>
          <div className="eyebrow mono">{data.hero.eyebrow}</div>
          <h1 className="serif">
            {data.hero.headline}
            <br />
            {data.hero.headlineMiddle} <em>{data.hero.headlineHighlight}</em>
          </h1>
          <p className="lead">{data.hero.leadText}</p>
          <WaitlistForm
            ctaLabel={data.hero.ctaLabel}
            copy={data.waitlist}
            liveCount={stats?.waitlistCount ?? null}
          />
          <p className="micro">{data.hero.microcopy}</p>
        </div>
        <div
          className="pulse-visual"
          style={
            data.hero.visualBackgroundUrl
              ? {
                  backgroundImage: `linear-gradient(160deg, rgba(10,9,16,0.72), rgba(10,9,16,0.92)), url(${data.hero.visualBackgroundUrl})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }
              : undefined
          }
        >
          <div className="pulse-label mono">{data.hero.visualLabel}</div>
          <div className="scan-wrap">
            <div className="scan-track">
              <svg width="600" height="120" viewBox="0 0 600 120" fill="none">
                <path
                  d="M0 60 L60 60 L80 60 L92 20 L104 100 L116 60 L180 60 L200 60 L212 25 L224 95 L236 60 L300 60 L320 60 L332 20 L344 100 L356 60 L420 60 L440 60 L452 25 L464 95 L476 60 L540 60 L560 60 L572 20 L584 100 L596 60 L600 60"
                  stroke="url(#g1)"
                  strokeWidth="2.5"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <defs>
                  <linearGradient id="g1" x1="0" y1="0" x2="600" y2="0">
                    <stop offset="0%" stopColor="#9B3A5C" />
                    <stop offset="50%" stopColor="#D9A44A" />
                    <stop offset="100%" stopColor="#9B3A5C" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
          </div>
          <div className="ticker mono">
            <span>
              <b>{stats?.openVenuesNow ?? "–"}</b> {data.hero.openVenuesLabel}
            </span>
            <span>
              <b>{stats?.curatedVenuesTotal ?? "–"}</b> {data.hero.curatedVenuesLabel}
            </span>
          </div>
        </div>
      </section>

      <section className="night" id="night">
        <div className="section-head">
          <div className="kicker mono">{data.nightSection.kicker}</div>
          <h2 className="serif">{data.nightSection.headline}</h2>
        </div>
        <div className="timeline">
          {data.timelineSteps.map((step) => (
            <div className="t-item" key={step.stepNumber}>
              <div className="t-time mono">{step.stepNumber}</div>
              <h3>{step.title}</h3>
              <p>{step.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="features">
        <div className="section-head">
          <div className="kicker mono">{data.featuresSection.kicker}</div>
          <h2 className="serif">{data.featuresSection.headline}</h2>
        </div>
        <div className="features-grid">
          {data.featureCards.map((card) => (
            <div className="feature-card" key={card.tag}>
              <div>
                <FeatureMockup type={card.mockupType} mockups={data.featureMockups} />
                <div className="ftag mono">{card.tag}</div>
                <h3 className="serif">{card.title}</h3>
              </div>
              <p>{card.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="venues" id="venues" style={{ paddingLeft: 0, paddingRight: 0 }}>
        <div className="label mono">{data.venuesSection.label}</div>
        <div className="marquee serif">
          {cats.map((c, i) => (
            <span key={`${c}-${i}`}>{c}</span>
          ))}
        </div>
      </section>

      <section className="outlook" id="outlook">
        <div className="section-head">
          <div className="kicker mono">{data.outlookSection.kicker}</div>
          <h2 className="serif">{data.outlookSection.headline}</h2>
        </div>
        <div className="roadmap-grid">
          {data.outlookCards.map((card) => (
            <div className="rmap-card" key={card.title}>
              <span className="rmap-badge mono">{card.badge}</span>
              <h3>{card.title}</h3>
              <p>{card.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="final">
        <h2 className="serif">{data.finalCta.headline}</h2>
        <p>{data.finalCta.subtext}</p>
        <WaitlistForm
          ctaLabel={data.hero.ctaLabel}
          copy={data.waitlist}
          liveCount={stats?.waitlistCount ?? null}
        />
      </section>

      <MarketingFooter footer={data.footer} logoUrl={logoUrl} />
    </div>
  );
}
